import { type FC, Suspense, useCallback, useEffect, useMemo, useState, memo, useRef } from 'react';
import { useThree } from '@react-three/fiber';
import * as THREE from 'three';
import { type OctreeGroups, type OctreeProgress } from '../../../types';
import { PointCloudMaterial } from '../../PointCloudMaterial';
import { BinaryHeap } from '../BinaryHeap';
import { Octree } from '../Octree';
import { OctreeNode } from '../OctreeNode';
import {
  OctreePointCloud,
  type OctreePointCloudExtendedProps,
  type OctreePointCloudProps,
} from './OctreePointCloud';
import { QueueItem } from './QueueItem';
import { PointCloudNode } from '../../PointCloudNode';
import { type FrameTimings } from '../../useFrameTimings';
import { ViewerFileType } from '../../Enums';
import { LoadErrorBoundary } from '../../LoadErrorBoundary';
import { useInterval } from '../../../helpers/hooks';
import { cameraPoseArray } from '../../Util';
import { getBoundingBoxOfRotatedVBox, type VisibilityBoxExtended } from '../../useVisibilityBox';
import { VisibilityBoxMode } from '../../VisibilityBox';
import { TransformTargetGroup } from '../../TransformTargetGroup';
import { useAbyssViewerContext } from '../../AbyssViewerContext';

const MINIMUM_NODE_PIXEL_SIZE = 0; // disabled

const DEFAULT_POINT_BUDGET = 20_000_000;

const MINIMUM_ADAPTIVE_BUDGET = 1_000_000;
const MAXIMUM_ADAPTIVE_BUDGET = 40_000_000;
const ADAPTIVE_STEP = 10;
const ADAPTIVE_BUDGET_PERIOD = 30_000;

const INITIAL_LOAD_NUMBER_OF_POINTS = 100_000;

export interface OctreePointCloudMasterProps {
  pointClouds?: OctreePointCloudProps[];
  showInSphericalView: boolean;
}

export interface OctreePointCloudMasterExtendedProps extends Omit<
  OctreePointCloudMasterProps,
  'pointClouds'
> {
  pointClouds?: Omit<
    OctreePointCloudExtendedProps,
    'addOctree' | 'removeOctree' | 'getPointCloudMaterial'
  >[];
  onInitialPointCloudLoad?: () => void;
  showInSphericalView: boolean;
  getPointCloudMaterial: (material: string) => PointCloudMaterial | undefined;
  visibilityBox: VisibilityBoxExtended;
  pointBudget?: number;
  invisiblePointsBudgetRatio?: number;
  enableAdaptivePointBudget?: boolean;
  onOctreeProgress?: (progress: OctreeProgress) => void;
  frameTimings: React.MutableRefObject<FrameTimings>;
  enableLoaderRetry: boolean;
  onFileLoadError?: (
    fileType: ViewerFileType,
    url: string,
    error: Error,
    info: React.ErrorInfo
  ) => void;
}

// Manages LoD for all Octrees on the scene
export class OctreePointCloudMasterImpl {
  private camera: THREE.PerspectiveCamera | THREE.OrthographicCamera | undefined;

  private renderer: THREE.WebGLRenderer | undefined;

  private octrees: Map<string, Octree> = new Map<string, Octree>();

  private octreeGroups: OctreeGroups;

  private lastCameraPosition: THREE.Vector3 = new THREE.Vector3();

  private rendererSize: THREE.Vector2 = new THREE.Vector2();

  private slope = 0;

  private halfHeight = 0;

  private pointBudget = DEFAULT_POINT_BUDGET;

  private invisiblePointBudget = 0;

  private adaptiveRatio = 1;

  public needsUpdate = false;

  private alignedVisibilityBox: THREE.Box3 | undefined;

  private queue: BinaryHeap<QueueItem> = new BinaryHeap<QueueItem>((x) => -x.weight);

  private frustum: THREE.Frustum = new THREE.Frustum();

  private frustumMatrix: THREE.Matrix4 = new THREE.Matrix4();

  private totalNumPoints: number = 0;

  constructor(octreeGroups: OctreeGroups) {
    // console.log('new OctreePointCloudMasterImpl');
    this.octreeGroups = octreeGroups;
  }

  getTotalNumPoints() {
    return this.totalNumPoints;
  }

  setCamera(camera: THREE.PerspectiveCamera | THREE.OrthographicCamera | undefined) {
    this.camera = camera;
  }

  setRenderer(renderer: THREE.WebGLRenderer) {
    this.renderer = renderer;
  }

  setVisibilityBox(visibilityBox: VisibilityBoxExtended) {
    if (visibilityBox.mode !== VisibilityBoxMode.Disabled) {
      this.alignedVisibilityBox = getBoundingBoxOfRotatedVBox(visibilityBox.matrix);
    } else {
      this.alignedVisibilityBox = undefined;
    }
  }

  setPointBudget(pointBudget: number, invisiblePointsBudgetRatio: number) {
    this.pointBudget = pointBudget;
    this.invisiblePointBudget = Math.trunc(
      pointBudget * Math.min(invisiblePointsBudgetRatio, 0.99)
    );
    this.needsUpdate = true;
  }

  setAdaptivePointBudgetRatio(adaptiveRatio: number): boolean {
    const minRatio = MINIMUM_ADAPTIVE_BUDGET / MAXIMUM_ADAPTIVE_BUDGET;
    const croppedRatio = Math.max(minRatio, Math.min(adaptiveRatio, 1));
    const steps0 = 20;
    const steps1 = steps0 * 2;
    const roundRatio = (steps: number) => {
      return Math.round(croppedRatio * steps) / steps;
    };
    const newRatio = croppedRatio > 0.3 ? roundRatio(steps0) : roundRatio(steps1);
    const hasNewRatio = this.adaptiveRatio !== newRatio;
    // if (hasNewRatio) {
    //   console.log('adaptiveRatio', newRatio);
    // }
    this.adaptiveRatio = newRatio;
    return hasNewRatio;
  }

  setAdaptivePointBudget(adaptivePointBudget: number): boolean {
    return this.setAdaptivePointBudgetRatio(adaptivePointBudget / this.pointBudget);
  }

  addOctree(octree: Octree) {
    this.octrees.set(octree.id, octree);
    this.needsUpdate = true;
    this.totalNumPoints += octree.totalNumPoints;
  }

  removeOctree(octree: Octree) {
    this.octrees.delete(octree.id);
    this.needsUpdate = true;
    this.totalNumPoints -= octree.totalNumPoints;
  }

  setOctreeGroup(octreeId: string, group: THREE.Group | null) {
    if (!this.octreeGroups) return;
    const groupInfo = this.octreeGroups.get(octreeId);
    if (!group) {
      if (groupInfo) {
        groupInfo.group = null;
      }
      return;
    }
    if (!groupInfo) {
      this.octreeGroups.set(octreeId, {
        group,
        matrix: new THREE.Matrix4(),
      });
    } else if (groupInfo.group !== group) {
      groupInfo.group = group;
    }
  }

  updateOctreeMatrixWorld(octreeId: string, matrix: THREE.Matrix4) {
    const octree = this.octrees.get(octreeId);
    if (!octree) {
      console.error('updateOctreeMatrixWorld octreeId not found', octreeId);
    }
    octree?.root.visitAll((node) => node.applyWorldMatrix(matrix));
  }

  private pushNodeToQueue(octree: Octree, octreeNode: OctreeNode) {
    const { lastCameraPosition, slope, halfHeight, alignedVisibilityBox } = this;
    if (alignedVisibilityBox && !alignedVisibilityBox.intersectsBox(octreeNode.worldBoundingBox)) {
      return;
    }
    if (octree.nodesToShow !== undefined && !octree.nodesToShow.has(octreeNode.key)) {
      return;
    }
    const sphere = octreeNode.worldBoundingSphere;
    const distance = sphere.center.distanceTo(lastCameraPosition);
    const { radius } = sphere;
    const projectionFactor = halfHeight / (slope * distance);
    const screenPixelRadius = radius * projectionFactor;

    // Don't add the node if it'll be too small on the screen.
    if (octreeNode === octree.root || screenPixelRadius >= MINIMUM_NODE_PIXEL_SIZE) {
      // Greater weight means higher priority in loading/displaying
      const weight = distance < radius ? Number.MAX_VALUE : screenPixelRadius;
      this.queue.push(new QueueItem(octree, octreeNode, weight));
    }
  }

  public calculateOctreeNodesToShow() {
    // TODO: can optimise by returning undefined when camera position hasn't changed much AND there's no nodes we're waiting to load
    // if (
    //   !this.needsUpdate ||
    //   this.lastCameraPosition.distanceToSquared(this.camera.position) < this.minCameraSqDistToUpdate
    // ) {
    //   return undefined;
    // }

    if (this.camera === undefined || this.renderer === undefined) {
      return [];
    }

    this.octreeGroups?.forEach((groupInfo, octreeId) => {
      if (groupInfo?.group && !groupInfo.group.matrixWorld.equals(groupInfo.matrix)) {
        // console.log(
        //   'updateOctreeMatrixWorld octreeGroups octreeId',
        //   octreeId,
        //   groupInfo.matrix.toArray(),
        //   groupInfo.group.matrixWorld.toArray()
        // );
        groupInfo.matrix.copy(groupInfo.group.matrixWorld);
        this.updateOctreeMatrixWorld(octreeId, groupInfo.matrix);
      }
    });

    this.frustumMatrix.multiplyMatrices(
      this.camera.projectionMatrix,
      this.camera.matrixWorldInverse
    );
    this.frustum.setFromProjectionMatrix(this.frustumMatrix);

    this.lastCameraPosition.copy(this.camera.position);

    this.halfHeight =
      0.5 * this.renderer.getSize(this.rendererSize).height * this.renderer.getPixelRatio();

    const fov =
      ((this.camera instanceof THREE.PerspectiveCamera ? this.camera.fov : 50) * Math.PI) / 180;
    this.slope = Math.tan(fov / 2);

    this.octrees.forEach((octree) => {
      if (octree.root !== null && octree.isVisible) {
        this.pushNodeToQueue(octree, octree.root);
      }
    });

    const invisiblePointBudget = this.adaptiveRatio * this.invisiblePointBudget;
    const visiblePointBudget = this.adaptiveRatio * this.pointBudget - invisiblePointBudget;

    let numberVisiblePoints = 0;
    let numberInvisiblePoints = 0;

    const nodesToLoad: OctreeNode[] = [];
    const invisibleNodesToLoad: OctreeNode[] = [];

    while (
      numberVisiblePoints < visiblePointBudget ||
      numberInvisiblePoints < invisiblePointBudget
    ) {
      const queueItem = this.queue.pop();
      if (queueItem == null) {
        break;
      }
      const { octreeNode, octree } = queueItem!;

      const isVisible =
        numberVisiblePoints < INITIAL_LOAD_NUMBER_OF_POINTS ||
        this.frustum.intersectsBox(octreeNode.worldBoundingBox);

      if (isVisible) {
        numberVisiblePoints += octreeNode.numPoints;
        if (numberVisiblePoints <= visiblePointBudget) {
          nodesToLoad.push(octreeNode);
        }
      } else {
        numberInvisiblePoints += octreeNode.numPoints;
        if (numberInvisiblePoints <= invisiblePointBudget) {
          invisibleNodesToLoad.push(octreeNode);
        }
      }
      octreeNode.children.forEach((childNode) => {
        this.pushNodeToQueue(octree, childNode);
      });
    }
    this.needsUpdate = false;
    this.queue.removeAll();
    // console.log({ nodesToLoad, octrees: this.octrees });
    // invisible nodes have lower priority
    nodesToLoad.push(...invisibleNodesToLoad);
    return nodesToLoad;
  }
}

const MAX_NODES_TO_LOAD_SIMULTANEOUSLY = 10;
const MIN_NODES_LOAD_STEP = 5;

export const OctreePointCloudMaster: FC<OctreePointCloudMasterExtendedProps> = memo(
  ({
    getPointCloudMaterial,
    pointClouds,
    onInitialPointCloudLoad,
    showInSphericalView,
    visibilityBox,
    pointBudget = DEFAULT_POINT_BUDGET,
    invisiblePointsBudgetRatio = 0,
    enableAdaptivePointBudget = true,
    onOctreeProgress,
    frameTimings,
    enableLoaderRetry,
    onFileLoadError,
  }) => {
    const { octreeGroups } = useAbyssViewerContext();
    const camera = useThree((state) => state.camera);
    const gl = useThree((state) => state.gl);
    const invalidate = useThree((state) => state.invalidate);

    const masterReference = useRef<OctreePointCloudMasterImpl>();

    useEffect(() => {
      if (octreeGroups) {
        masterReference.current = new OctreePointCloudMasterImpl(octreeGroups);
      }
    }, [octreeGroups]);

    const master = masterReference.current;

    invalidate();

    useEffect(() => {
      if (!master) return;
      if (camera instanceof THREE.PerspectiveCamera || camera instanceof THREE.OrthographicCamera) {
        master.setCamera(camera);
      } else {
        master.setCamera(undefined);
      }
    }, [master, camera]);

    useEffect(() => {
      master?.setRenderer(gl);
    }, [master, gl]);

    useEffect(() => {
      const budget = enableAdaptivePointBudget ? MAXIMUM_ADAPTIVE_BUDGET : pointBudget;
      master?.setPointBudget(budget, invisiblePointsBudgetRatio);
    }, [master, pointBudget, invisiblePointsBudgetRatio, enableAdaptivePointBudget]);

    useEffect(() => {
      if (!master) return;
      master.setVisibilityBox(visibilityBox);
      master.needsUpdate = true;
    }, [master, visibilityBox]);

    const pointCloudsById = useMemo(
      () => new Map(pointClouds?.map((pointCloud) => [pointCloud.id, pointCloud])),
      [pointClouds]
    );

    const [octreeNodesToShow, setOctreeNodesToShow] = useState<Set<OctreeNode>>(new Set());
    const completeNodesReference = useRef<Set<OctreeNode>>(new Set());
    const loadingNodesReference = useRef<Set<OctreeNode>>(new Set());
    const errorNodesReference = useRef<Map<OctreeNode, number>>(new Map());
    const nodeDelayReference = useRef<Map<OctreeNode, number>>(new Map());

    const [octreeNodes, setOctreeNodes] = useState<OctreeNode[]>([]);
    const octreeNodesReference = useRef<OctreeNode[]>([]);

    const loadedNumberPoints = useRef(0);
    const loadedNumberNodes = useRef(0);
    const hasInitialLoadTriggered = useRef(false);

    const adaptivePower = useRef(4);

    const calcNodesToShow = useCallback(() => {
      if (master === null) {
        return;
      }
      if (!master) return;
      const newToShowArray = master.calculateOctreeNodesToShow();
      setOctreeNodesToShow((oldOctreeNodesToShow) => {
        if (
          newToShowArray &&
          newToShowArray.length === oldOctreeNodesToShow.size &&
          newToShowArray.every((node) => oldOctreeNodesToShow.has(node))
        ) {
          return oldOctreeNodesToShow;
        }
        return new Set(newToShowArray);
      });
    }, [master]);

    const prevCameraPose = useRef<number[]>([]);

    const hasSameCameraPose = useCallback(() => {
      const prevPose = prevCameraPose.current;
      const pose = cameraPoseArray(camera);
      prevCameraPose.current = pose;
      if (!prevPose) return true;

      return !pose.some((value, index) => value !== prevPose[index]);
    }, [camera]);

    const cameraPoseChangeTime = useRef<number>(performance.now());

    useEffect(() => {
      if (!master) return;
      if (!enableAdaptivePointBudget) {
        master.setAdaptivePointBudgetRatio(1);
      }
    }, [master, enableAdaptivePointBudget]);

    const updateAdaptiveBudget = useCallback(() => {
      if (!master) return;
      if (!enableAdaptivePointBudget) return;

      const currentTime = performance.now();
      if (!hasSameCameraPose()) {
        cameraPoseChangeTime.current = currentTime;
        return;
      }
      if (currentTime > cameraPoseChangeTime.current + ADAPTIVE_BUDGET_PERIOD) {
        return;
      }

      // budget auto adjustment
      const { avgFrameRenderingTime3 } = frameTimings.current;

      const pw = Math.max(0, Math.round(avgFrameRenderingTime3 / ADAPTIVE_STEP) - 1);

      const k0 = 1 / 20;
      adaptivePower.current = pw * k0 + (1 - k0) * adaptivePower.current;
      // assuming ADAPTIVE_STEP=10 and MAXIMUM_ADAPTIVE_BUDGET=40
      // this produces budgets (decreasing exponentioally):
      //   - 20M for 20 millisecons
      //   - 5M for 40 milliseconds
      // const adaptiveRatio = (1/2) ** adaptivePower.current;
      const adaptiveRatio = Math.exp(-adaptivePower.current * Math.LN2);
      const hasNewAdaptiveRatio = master.setAdaptivePointBudgetRatio(adaptiveRatio);
      if (hasNewAdaptiveRatio) {
        calcNodesToShow();
      }
    }, [master, frameTimings, calcNodesToShow, enableAdaptivePointBudget, hasSameCameraPose]);

    const canLoadNode = useCallback(
      (node: OctreeNode) => {
        const resumeTime = errorNodesReference.current.get(node);
        if (!resumeTime) return true;
        const currentTime = performance.now();
        if (enableLoaderRetry && currentTime > resumeTime) {
          errorNodesReference.current.delete(node);
          return true;
        }
        return false;
      },
      [enableLoaderRetry]
    );

    const refreshOctreeNodes = useCallback(
      (nodesToShow: Set<OctreeNode>) => {
        const completeNodes = completeNodesReference.current;
        const loadingNodes = loadingNodesReference.current;

        const newCompleteNodes = new Set<OctreeNode>();
        const newLoadingNodes = new Set<OctreeNode>();
        const newOctreeNodes: OctreeNode[] = [];

        const filteredNodesToShow = Array.from(nodesToShow).filter((node) => canLoadNode(node));

        // Effectively removing nodes from loadingNodes and completeNodes that shouldn't be shown
        filteredNodesToShow.forEach((node: OctreeNode) => {
          if (completeNodes.has(node)) {
            newCompleteNodes.add(node);
          } else if (loadingNodes.has(node)) {
            newLoadingNodes.add(node);
          }
        });

        // Adding all nodes that in complete or loading state already
        // and limited amount of new nodes to load
        let loadingCount = newLoadingNodes.size;

        // will start load new nodes when can start loading at least MIN_NODES_LOAD_STEP nodes
        if (MAX_NODES_TO_LOAD_SIMULTANEOUSLY - newLoadingNodes.size <= MIN_NODES_LOAD_STEP) {
          loadingCount = MAX_NODES_TO_LOAD_SIMULTANEOUSLY;
        }
        filteredNodesToShow.forEach((node: OctreeNode) => {
          if (newCompleteNodes.has(node) || newLoadingNodes.has(node)) {
            newOctreeNodes.push(node);
          } else if (loadingCount < MAX_NODES_TO_LOAD_SIMULTANEOUSLY) {
            newOctreeNodes.push(node);
            newLoadingNodes.add(node);
            loadingCount += 1;
          }
        });

        completeNodesReference.current = newCompleteNodes;
        loadingNodesReference.current = newLoadingNodes;

        if (!loadingCount) {
          calcNodesToShow();
        }

        // console.log(
        //   'refreshOctreeNodes',
        //   nodesToShow.size,
        //   newOctreeNodes.length,
        //   completeNodesReference.current.size,
        //   loadingNodesReference.current.size
        // );

        // keeping octreeNodesReference copy to avoid octreeNodes hook dependency
        const oldOctreeNodes = octreeNodesReference.current;
        if (
          newOctreeNodes.length === oldOctreeNodes.length &&
          newOctreeNodes.every((node, index) => oldOctreeNodes[index] === node)
        ) {
          return;
        }
        octreeNodesReference.current = newOctreeNodes;
        setOctreeNodes(newOctreeNodes);
      },
      [calcNodesToShow, canLoadNode]
    );
    const octreeNodesByPointCloud: { [octreeId: string]: OctreeNode[] } = useMemo(() => {
      const result: { [octreeId: string]: OctreeNode[] } = {};
      octreeNodes.forEach((node) => {
        if (node.octreeId in result) {
          result[node.octreeId].push(node);
        } else {
          result[node.octreeId] = [node];
        }
      });
      return result;
    }, [octreeNodes]);

    useEffect(() => {
      refreshOctreeNodes(octreeNodesToShow);
    }, [octreeNodesToShow, refreshOctreeNodes]);

    useInterval(() => {
      updateAdaptiveBudget();
      calcNodesToShow();
    }, 500);

    const addOctreeCB = useCallback(
      (octree: Octree) => {
        master?.addOctree(octree);
      },
      [master]
    );

    const removeOctreeCB = useCallback(
      (octree: Octree) => {
        master?.removeOctree(octree);
      },
      [master]
    );

    const nullFunctionCallback = useCallback(() => {}, []);

    const handleNodeUnmounted = useCallback(
      (octreeNode?: OctreeNode) => {
        if (!octreeNode) {
          return;
        }
        loadedNumberPoints.current -= octreeNode.numPoints;
        loadedNumberNodes.current -= 1;
        loadingNodesReference.current.delete(octreeNode);
        completeNodesReference.current.delete(octreeNode);
        if (onOctreeProgress) {
          onOctreeProgress({
            loaded: {
              points: loadedNumberPoints.current,
              nodes: loadedNumberNodes.current,
            },
          });
        }
      },
      [onOctreeProgress]
    );

    const handleNodeFileLoaded = useCallback(
      (octreeNode?: OctreeNode) => {
        if (!octreeNode) {
          return;
        }
        loadedNumberPoints.current += octreeNode.numPoints;
        loadedNumberNodes.current += 1;
        loadingNodesReference.current.delete(octreeNode);
        completeNodesReference.current.add(octreeNode);

        errorNodesReference.current.delete(octreeNode);
        nodeDelayReference.current.delete(octreeNode);

        if (!hasInitialLoadTriggered.current) {
          const totlaNumPoints = master?.getTotalNumPoints() ?? 0;
          const limit =
            totlaNumPoints == 0
              ? INITIAL_LOAD_NUMBER_OF_POINTS
              : Math.min(totlaNumPoints, INITIAL_LOAD_NUMBER_OF_POINTS);

          if (loadedNumberPoints.current >= limit) {
            hasInitialLoadTriggered.current = true;
            onInitialPointCloudLoad?.();
          }
        }

        refreshOctreeNodes(octreeNodesToShow);
        if (onOctreeProgress) {
          onOctreeProgress({
            loaded: {
              points: loadedNumberPoints.current,
              nodes: loadedNumberNodes.current,
            },
          });
        }
      },
      [refreshOctreeNodes, onOctreeProgress, octreeNodesToShow, onInitialPointCloudLoad, master]
    );

    const handleLoadError = useCallback(
      (octreeNode: OctreeNode, error: Error, info: React.ErrorInfo) => {
        const delayMsec = (nodeDelayReference.current.get(octreeNode) ?? 500) * 2;
        nodeDelayReference.current.set(octreeNode, delayMsec);
        errorNodesReference.current.set(octreeNode, performance.now() + delayMsec);

        if (onFileLoadError) {
          onFileLoadError(ViewerFileType.POINT_CLOUD_NODE, octreeNode.url, error, info);
        } else {
          console.warn('Octree node load error ignored', { octreeNode, error, info });
        }
      },
      [onFileLoadError]
    );

    const octreeNodesComp = useMemo(
      () =>
        pointClouds?.map((pointCloud) => {
          const { position, rotation, scale, rotationCenter } = pointCloud;
          return (
            <TransformTargetGroup
              targetId={pointCloud.id}
              position={position}
              rotation={rotation}
              scale={scale}
              rotationCenter={rotationCenter}
              key={pointCloud.id}
            >
              <group ref={(gr) => master?.setOctreeGroup(pointCloud.id, gr)}>
                {octreeNodesByPointCloud[pointCloud.id]?.map((octreeNode) => (
                  <LoadErrorBoundary
                    onError={(error: Error, info: React.ErrorInfo) =>
                      handleLoadError(octreeNode, error, info)
                    }
                    key={`${octreeNode.octreeId}-${octreeNode.key}-boundary`}
                  >
                    <Suspense fallback={null}>
                      <PointCloudNode
                        octreeId={pointCloud.id}
                        octreeNode={octreeNode}
                        url={octreeNode.url}
                        requestHeader={octreeNode.requestHeader}
                        name=""
                        dataFormat={octreeNode.dataFormat}
                        key={`${octreeNode.octreeId}-${octreeNode.key}`}
                        material={pointCloudsById.get(octreeNode.octreeId)?.material ?? 'color'}
                        showInSphericalView={showInSphericalView}
                        isVisible
                        getPointCloudMaterial={getPointCloudMaterial}
                        onViewerObjectReady={nullFunctionCallback}
                        onUnmount={handleNodeUnmounted}
                        onNodeFileLoaded={handleNodeFileLoaded}
                      />
                    </Suspense>
                  </LoadErrorBoundary>
                ))}
              </group>
            </TransformTargetGroup>
          );
        }),
      [
        master,
        pointClouds,
        octreeNodesByPointCloud,
        pointCloudsById,
        showInSphericalView,
        getPointCloudMaterial,
        nullFunctionCallback,
        handleNodeUnmounted,
        handleNodeFileLoaded,
        handleLoadError,
      ]
    );

    return (
      <>
        {pointClouds?.map(
          (pointCloud) =>
            (pointCloud.isVisible ?? true) && (
              <Suspense fallback={null} key={pointCloud.id}>
                <OctreePointCloud
                  {...pointCloud}
                  addOctree={addOctreeCB}
                  removeOctree={removeOctreeCB}
                  key={pointCloud.id}
                />
              </Suspense>
            )
        )}
        {octreeNodesComp}
      </>
    );
  }
);
OctreePointCloudMaster.displayName = 'OctreePointCloudMaster';
